import type {
  BusinessCenterIntelligencePayload,
  BusinessCenterIntelligenceWidgetDescriptor,
} from "./types";

export function buildDemoBusinessCenterIntelligence(
  scope: BusinessCenterIntelligencePayload["scope"],
  variant: "admin" | "client" = "admin",
): BusinessCenterIntelligencePayload {
  const isClient = variant === "client";
  const widgets: BusinessCenterIntelligenceWidgetDescriptor[] = [
    { key: "business-health", label: "Business Health", description: "Overall health and risk posture.", placement: "hero", visible: true, order: 10 },
    { key: "ai-recommendations", label: "AI Recommendations", description: "Actionable guidance from the AI layer.", placement: "grid", visible: true, order: 20 },
    { key: "marketplace", label: "Marketplace Recommendations", description: "Solutions, templates, assistants, and integrations.", placement: "grid", visible: true, order: 30 },
    { key: "workspace-insights", label: "Workspace Intelligence", description: "Growth, activity, and engagement trends.", placement: "grid", visible: true, order: 40 },
    { key: "client-journey", label: "Client Journey", description: "Milestones and delivery progress.", placement: "sidebar", visible: isClient, order: 50 },
    { key: "project-intelligence", label: "Project Intelligence", description: "Project health, forecast, and risk.", placement: "grid", visible: true, order: 60 },
    { key: "memory-media-vault", label: "Memory Media Vault Intelligence", description: "Storage, archive readiness, and preservation signals.", placement: "grid", visible: true, order: 70 },
    { key: "subscription-intelligence", label: "Subscription Intelligence", description: "Plan, usage, and entitlement posture.", placement: "grid", visible: true, order: 80 },
    { key: "admin-intelligence", label: "Admin Intelligence", description: "Platform, product, and growth summary.", placement: "sidebar", visible: !isClient, order: 90 },
  ];

  return {
    scope,
    widgets,
    health: {
      businessHealthScore: isClient ? 93 : 91,
      workspaceHealthScore: isClient ? 88 : 90,
      growthScore: isClient ? 79 : 83,
      efficiencyScore: isClient ? 84 : 86,
      adoptionScore: isClient ? 87 : 89,
      revenueTrend: "up",
      projectsAtRisk: isClient ? 1 : 3,
      clientsRequiringFollowUp: isClient ? 1 : 2,
      platformHealth: "Platform health 91/100",
    },
    recommendations: [
      {
        key: "demo-executive-kpi",
        title: "Executive KPI Dashboard",
        category: "workspace",
        summary: "Open a compact KPI view for leadership review.",
        explanation: "Surface the most important health and revenue metrics in a single panel.",
        estimatedImpact: "Improves operational visibility.",
        estimatedTimeSavingsMinutes: 30,
        actionLabel: "View Details",
        actionUrl: "/admin/dashboard",
        confidence: 0.94,
        source: "ai",
        installed: true,
      },
      {
        key: "demo-customer-retention",
        title: "Customer Retention Workflow",
        category: "client_success",
        summary: "Follow up on clients needing attention.",
        explanation: "Use the client journey and activity feed to create a retention follow-up routine.",
        estimatedImpact: "Reduces churn risk.",
        estimatedTimeSavingsMinutes: 25,
        actionLabel: "Configure",
        actionUrl: "/client/dashboard",
        confidence: 0.91,
        source: "ai",
        installed: false,
      },
    ],
    marketplace: {
      recommendedSolutions: [
        {
          key: "demo-mmv",
          group: "recommended",
          title: "Memory Media Vault",
          category: "media_suite",
          description: "Review, approval, delivery, archive, and preservation workflows.",
          status: "active",
          actionLabel: "View solution",
          actionUrl: "/admin/memory-media-vault",
          installed: true,
          featured: true,
          sortOrder: 10,
          analyticsCount: 42,
          assignmentCount: 3,
        },
      ],
      newReleases: [],
      popularTemplates: [],
      trendingSolutions: [],
      installedProducts: [],
      industryPacks: [],
      aiAssistants: [
        {
          key: "demo-ai-assistant",
          group: "ai_assistants",
          title: "AI Business Analyst",
          category: "assistant",
          description: "Turn scores and recommendations into an executive summary.",
          status: "active",
          actionLabel: "Open AI",
          actionUrl: "/admin/ai",
          installed: true,
          featured: true,
          sortOrder: 10,
          analyticsCount: 0,
          assignmentCount: 0,
        },
      ],
      integrations: [
        {
        key: "demo-clientflow",
          group: "integrations",
          title: "ClientFlow Studio",
          category: "platform_tool",
          description: "Workspace provisioning and portal experience engine.",
          status: "active",
          actionLabel: "Open Studio",
          actionUrl: "/admin/workspace-provisioning-center",
          installed: true,
          featured: true,
          sortOrder: 10,
          analyticsCount: 0,
          assignmentCount: 0,
        },
      ],
    },
    workspaceInsights: {
      workspaceGrowth: "Growth is trending upward.",
      usageTrend: "Usage is trending up.",
      activityTrend: "Activity remains active.",
      engagementTrend: "Engagement is healthy.",
      activeWorkspaces: 4,
      activeProducts: 6,
      activeSubscriptions: 3,
      adoptionHighlights: [
        "Core platform services are active",
        "Client portal usage is visible",
        "Marketplace and AI recommendations are ready",
      ],
    },
    clientJourney: isClient
      ? {
          currentStage: "Proposal Accepted",
          completionPercent: 68,
          upcomingMilestones: ["Agreement Signed", "Deposit Received", "Project Started"],
          deliverables: ["Proposal", "Agreement", "Project timeline"],
          archiveEligible: false,
          proposalToProjectRelationship: "Accepted proposals can automatically launch projects.",
          deliveryToArchiveRelationship: "Completed delivery can route into archive workflows.",
          revisionRequestWorkflow: "Revision requests return the proposal to review and notify the owner.",
          acceptanceWorkflow: "Acceptance triggers project creation and portal access.",
          timeline: [
            {
              id: "demo-journey-1",
              kind: "conversion",
              title: "Proposal Accepted",
              detail: "Client approved the proposal and the delivery workflow can begin.",
              createdAt: new Date().toISOString(),
              relatedUrl: "/client/portal",
            },
          ],
        }
      : null,
    projectIntelligence: {
      projectHealthScore: isClient ? 88 : 84,
      delayRisks: isClient ? 1 : 3,
      completionForecast: "Delivery is tracking toward the current target.",
      deliverableStatus: ["Draft ready", "Review pending", "Final delivery on track"],
      milestoneProgress: ["Discovery Complete", "Proposal Accepted", "Project Started"],
    },
    memoryMediaVault: {
      storageUsageBytes: 612_000_000,
      storageLimitBytes: 5_000_000_000,
      archiveReadiness: "Archive readiness high",
      preservationCandidates: 12,
      upgradeRecommendations: [
        "Increase storage to support future media volume.",
        "Review archive candidates before final delivery.",
      ],
    },
    subscription: {
      currentPlan: "Business",
      usageSummary: ["user_limit 18 / 50 users", "workspace_limit 5 / 20 workspaces"],
      remainingCapacity: ["user_limit: 32 remaining", "workspace_limit: 15 remaining"],
      upgradeSuggestions: ["Upgrade when storage or user growth increases."],
      overageWarnings: ["No overage warnings at this time."],
    },
    platform: {
      platformHealth: "Platform health 91/100",
      productAdoption: 6,
      marketplaceActivity: 18,
      aiRecommendationTrend: "up",
      subscriptionGrowth: "3 active subscriptions",
      mmvUsage: "12% storage used",
    },
  };
}
